import { type BannerVariant } from "@jsq/ds/deprecated";
import {
  type ArenaObjectType,
  type GetGlobalUserSettingsQuery,
  type LocationObjectType,
  type ProductType,
  type TwoFaType as TwoFaTypeEnum,
} from "common/types/mainTypes";
import { type GeoErrorsCode as GeoErrorsCodeTemp } from "apps/portal/Portal/common/Map/utils";
import {
  type ALL_INVESTMENT_LIST_COLUMNS_TEMP,
  type DATE_FORMATS,
  type MONEY_INVESTMENT_LIST_COLUMNS,
  type TIME_FORMATS,
} from "./constants";

export enum PaymentPrefType {
  ACH = "ach",
  Wire = "wire",
  Check = "check",
  EFT = "eft",
  SEPA = "sepa",
  BECS = "becs",
  BACS = "bacs",
}
export type AlertMessage = {
  variant: BannerVariant;
  message: string;
};

export type Arena = {
  id: string;
  name: string;
  domain: string;
};

export type SingleManagerContextRouteParams = {
  arenaDomain: string;
  childEntityId?: string;
};

export type SingleManagerPositionRouteParams = SingleManagerContextRouteParams & {
  positionId: string;
};
export type InvestmentListColumnName = (typeof ALL_INVESTMENT_LIST_COLUMNS_TEMP)[number];
export type MoneyInvestmentListColumnName = (typeof MONEY_INVESTMENT_LIST_COLUMNS)[number];
export type UnauthenticatedArenaObjectType = Pick<
  ArenaObjectType,
  | "id"
  | "name"
  | "domain"
  | "contactEmail"
  | "contactPhone"
  | "websiteUrl"
  | "signinButtonTextColor"
  | "signinButtonBackgroundColor"
  | "signInButtonTextColor"
  | "signInButtonBackgroundColor"
  | "signInPhotoUrl"
  | "signInAccessMessage"
  | "logoUrl"
  | "logoWideUrl"
  | "headerLogoUrl"
  | "headerLogoUrl2x"
  | "secondLevelDomain"
  | "loginMessage"
  | "faviconUrl16px"
  | "faviconUrl32px"
  | "hideCopyrightText"
>;

// A flattened Asset/Location representation

export type AssetLocationTypeTemp = {
  id: string;
  name: string;
  manager: string;
  errorLocation: GeoErrorsCodeTemp;
  city: string;
  street: string;
  street2: string;
  street3: string;
  codeState: string;
  state: string;
  postalCode: string;
  productTypes: ProductType[];
  latitude: number;
  longitude: number;
  yearBuilt: string;
  entityName: string;
  arenaName: string;
  photoUrl: string;
  objectId: string;
  positionId: string | undefined;
};

export type AssetLatLong = {
  id: string;
  positionId: number;
  latitude: number;
  longitude: number;
};

export type AssetLocationTypeDeprecated = {
  id: string;
  name: string;
  manager: string;
  errorLocation: GeoErrorsCodeTemp;
  city: string;
  street: string;
  street2: string;
  street3: string;
  codeState: string;
  state: string;
  postalCode: string;
  productTypes: ProductType[];
  latitude: number;
  longitude: number;
  yearBuilt: string;
  entityName: string;
  arenaName: string;
  photoUrl: string;
  objectId: string;
  positionId: string | undefined;
};

export type LocationType = Pick<
  LocationObjectType,
  | "id"
  | "latitude"
  | "longitude"
  | "caption"
  | "city"
  | "street"
  | "street2"
  | "street3"
  | "postalCode"
  | "countryId"
  | "stateId"
  | "region"
>;

export interface Control2FAMethodSelection {
  /**
   * The current 2FA method that is selected.
   */
  current2FAMethod: TwoFaTypeEnum;

  /**
   * A callback to change the 2FA setup component that is displayed, to an email method.
   */
  showEmail2FAMethod?: () => void;

  /**
   * A callback to change the 2FA setup component that is displayed, to an sms method.
   */
  showSMS2FAMethod?: () => void;

  /**
   * A callback to change the 2FA setup component that is displayed, to a totp method.
   */
  showTotp2FAMethod?: () => void;

  /**
   * A boolean used to determine if we are showing the 2FA method selection screen
   */
  isShowing2FAMethodSelection: boolean;

  /**
   * A callback used to determine if we are setting up 2FA for activation or upgrade flow
   */
  show2FAMethodSelection?: (show: boolean) => void;

  /**
   * A boolean used to determine if we are loading the available 2FA methods
   */
  isLoading: boolean;
}

export type DateFormatType = keyof typeof DATE_FORMATS | null | undefined;

export type DateFormatStringType = { long: string; short: string };

export type TimeFormatType = keyof typeof TIME_FORMATS | null | undefined;

export type MatchParams = {
  accountId: string;
};

export type CurrentGlobalUserType =
  NonNullable<GetGlobalUserSettingsQuery>[`currentGlobalUser`];
